import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, CalculateOutlined, LockOutlined } from '@mui/icons-material';
import colors from '../../colors.styles';
import adminFormStyles from '../../shared/styles/adminForm.styles';

const TARGETING_FIELDS = [
  { label: 'Store type', control: 'filter', placeholder: 'Add type' },
  { label: 'State', control: 'filter', placeholder: 'Add state' },
  { label: 'Regions', control: 'input', placeholder: 'Add region', width: 164 },
  {
    label: 'Market number',
    control: 'input',
    placeholder: 'Add market #',
    width: 164,
  },
  {
    label: 'Store number',
    control: 'input',
    placeholder: 'Add store #',
    width: 164,
  },
  {
    label: 'Users',
    control: 'users',
    placeholder: 'Add specific user',
    width: 220,
  },
];

const styles = {
  section: adminFormStyles.section,
  sectionTitle: adminFormStyles.sectionTitle,
  optionalLabel: adminFormStyles.optionalLabel,
  sectionDescription: adminFormStyles.sectionDescription,
  alert: {
    alignItems: 'flex-start',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '12px',
    color: '#92400e',
    fontSize: '14px',
    py: 0.5,
    '& .MuiAlert-icon': { color: '#b45309', mt: 0.25 },
  },
  lockedAlert: {
    alignItems: 'flex-start',
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    color: '#656d80',
    fontSize: '14px',
    py: 0.5,
    '& .MuiAlert-icon': { color: '#656d80', mt: 0.25 },
  },
  lockedContent: {
    opacity: 0.6,
  },
  targetingRow: {
    borderBottom: '1px solid #e9ecee',
    minHeight: 64,
    py: 1.25,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  targetingLabel: {
    color: '#041f41',
    flex: '0 0 140px',
    fontSize: '14px',
    fontWeight: 700,
  },
  addIcon: {
    fontSize: '16px !important',
  },
  disabledFilter: {
    borderColor: '#a7b1c0',
    borderRadius: '20px',
    borderStyle: 'dashed',
    fontSize: '14px',
    fontWeight: 700,
    minHeight: 42,
    px: 2,
    textTransform: 'none',
    '&.Mui-disabled': {
      borderColor: '#a7b1c0',
      color: colors.base.walmart_blue,
    },
  },
  disabledInput: (width) => ({
    width,
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.base.light,
      borderRadius: '8px',
      fontSize: '14px',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: '#656d80',
    },
  }),
  calculateButton: {
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    justifyContent: 'flex-start',
    minHeight: 48,
    px: 1.5,
    textTransform: 'none',
    '&.Mui-disabled': { color: colors.base.walmart_blue },
  },
};

export default function TaskTargetingPlaceholder({ isLocked }) {
  return (
    <Paper component="section" variant="outlined" sx={styles.section}>
      <Stack spacing="20px" padding="20px">
        <Box>
          <Typography component="h2" sx={styles.sectionTitle}>
            Targeting{' '}
            <Typography component="span" sx={styles.optionalLabel}>
              optional
            </Typography>
          </Typography>
          <Typography sx={styles.sectionDescription}>
            Choose which stores receive this request.
          </Typography>
        </Box>

        {isLocked ? (
          <Alert
            icon={<LockOutlined fontSize="small" />}
            severity="info"
            sx={styles.lockedAlert}
          >
            <strong>Targeting is locked.</strong> It can&apos;t be changed after
            a request has launched.
          </Alert>
        ) : (
          <Alert severity="warning" sx={styles.alert}>
            <strong>Targeting is coming soon.</strong> This request will submit
            without targeting filters.
          </Alert>
        )}

        <Box sx={isLocked ? styles.lockedContent : undefined}>
          <Stack>
            {TARGETING_FIELDS.map(({ label, control, placeholder, width }) => (
              <Stack
                key={label}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
                sx={styles.targetingRow}
              >
                <Typography sx={styles.targetingLabel}>{label}</Typography>
                {control === 'filter' && (
                  <Button
                    disabled
                    startIcon={<Add sx={styles.addIcon} />}
                    variant="outlined"
                    sx={styles.disabledFilter}
                  >
                    {placeholder}
                  </Button>
                )}
                {control === 'input' && (
                  <TextField
                    disabled
                    hiddenLabel
                    placeholder={placeholder}
                    size="small"
                    sx={styles.disabledInput(width)}
                  />
                )}
                {control === 'users' && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      disabled
                      startIcon={<Add sx={styles.addIcon} />}
                      variant="outlined"
                      sx={styles.disabledFilter}
                    >
                      Add pay type
                    </Button>
                    <TextField
                      disabled
                      hiddenLabel
                      placeholder={placeholder}
                      size="small"
                      sx={styles.disabledInput(width)}
                    />
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>

        <Button
          disabled
          fullWidth
          startIcon={<CalculateOutlined />}
          sx={{
            ...styles.calculateButton,
            ...(isLocked ? styles.lockedContent : {}),
          }}
        >
          Calculate my reach
        </Button>
      </Stack>
    </Paper>
  );
}

TaskTargetingPlaceholder.propTypes = {
  isLocked: PropTypes.bool,
};

TaskTargetingPlaceholder.defaultProps = {
  isLocked: false,
};
